// SkyTracker @skytracker/web — top-level app shell.
import { lazy, Suspense, useEffect, useMemo, useRef } from 'react';
import { hasWebGL2 } from './globe/webgl-detect.js';
import { TelemetryPanel } from './panels/TelemetryPanel.js';
import { FilterPanel } from './panels/FilterPanel.js';
import { StatusBar } from './panels/StatusBar.js';
import { useFleet } from './store/useFleet.js';
import { SkyTrackerWs } from './ws/client.js';
import { filtersFromUrl, syncFiltersToUrl } from './lib/url-params.js';

// Code-split: Cesium (~3 MB minified) loads only when WebGL2 is present;
// Leaflet+Pixi (~200 KB) loads only when it isn't. Saves the cold-load
// cost of the unused renderer.
const CesiumGlobe = lazy(() =>
  import('./globe/CesiumGlobe.js').then((m) => ({ default: m.CesiumGlobe })),
);
const LeafletMap = lazy(() =>
  import('./leaflet/LeafletMap.js').then((m) => ({ default: m.LeafletMap })),
);

function RendererLoading(): JSX.Element {
  return (
    <div
      className="globe-host"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--spectre-text-dim)',
        fontFamily: 'var(--font-mono)',
        fontSize: 12,
        letterSpacing: '0.1em',
      }}
    >
      LOADING GLOBE…
    </div>
  );
}

const WS_URL = (import.meta.env.VITE_WS_URL as string | undefined) ?? `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;

export function App(): JSX.Element {
  const webGL2 = useMemo(() => hasWebGL2(), []);
  const setFilters = useFleet((s) => s.setFilters);
  const setPanelLeftOpen = useFleet((s) => s.setPanelLeftOpen);
  const filters = useFleet((s) => s.filters);
  const bbox = useFleet((s) => s.bbox);
  const wsRef = useRef<SkyTrackerWs | null>(null);

  useEffect(() => {
    const initial = filtersFromUrl();
    if (Object.keys(initial).length > 0) setFilters(initial);
  }, [setFilters]);

  useEffect(() => {
    syncFiltersToUrl(filters);
  }, [filters]);

  useEffect(() => {
    const fleet = useFleet.getState();
    const ws = new SkyTrackerWs(WS_URL, {
      onSnapshot: (positions, feedStatus, truncated) =>
        fleet.applySnapshot(positions, feedStatus, truncated),
      onUpdate: (positions) => fleet.applyUpdate(positions),
      onRemove: (ids) => fleet.applyRemove(ids),
      onStatus: (feed) => fleet.setFeedStatus(feed),
    });
    ws.start();
    wsRef.current = ws;
    return () => ws.stop();
  }, []);

  useEffect(() => {
    if (wsRef.current) wsRef.current.subscribe(bbox, filters);
  }, [bbox, filters]);

  return (
    <div className="skytracker-app">
      <header className="topbar">
        <div>
          <span className="brand">SKYTRACKER</span>
          <span className="brand-version">v0.1.0</span>
        </div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--spectre-text-dim)', letterSpacing: '0.1em' }}>
          ATMOSPHERIC TRAFFIC · LIVE
        </div>
      </header>

      {!webGL2 && (
        <div className="fallback-banner">
          WEBGL2 UNAVAILABLE — RUNNING LEAFLET FALLBACK (REDUCED FIDELITY)
        </div>
      )}

      <Suspense fallback={<RendererLoading />}>
        {webGL2 ? <CesiumGlobe /> : <LeafletMap />}
      </Suspense>

      <FilterPanel />
      <TelemetryPanel />
      <StatusBar />

      <button className="fab" onClick={() => setPanelLeftOpen(true)} aria-label="open filters">▤</button>
    </div>
  );
}
